import { portalPaths } from '@provision/portal-routes';
import { isNullish } from 'remeda';
import { EllipsesMenuOption } from 'src/components/EllipsesMenu/ellipses-menu';
import { usePartners } from 'src/graphql';
import {
  Notification,
  NotificationStatus,
  useUpdateNotification,
} from 'src/graphql/entities/notification';
import {
  getNotificationConversationFriendlyId,
  getNotificationEntityName,
} from 'src/graphql/entities/notification/notification-target';
import { EntityNames } from 'src/graphql/types';
import { getPartnerFriendlyIdentifier } from 'src/routes/hooks';
import { Modals, useModalMethods } from 'src/store/modals';
import { useCopyToClipboard } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';

export const useNotificationMethods = () => {
  const { partners } = usePartners();
  const { updateNotificationsByCondition } = useUpdateNotification();
  const { showModal: showSnoozeModal } = useModalMethods(Modals.SnoozeConversationModal);
  const { navigate } = useNavigateRoute();

  const { copyToClipboard } = useCopyToClipboard();

  const toggleRead = (notification: Notification) => {
    // Toggle read/unread for notifications in the same scope (entity or single record).
    const newStatus =
      notification.status === NotificationStatus.Read
        ? NotificationStatus.Unread
        : NotificationStatus.Read;

    updateNotificationsByCondition({
      conditions: getNotificationScopeConditions(notification),
      patch: { status: newStatus },
    });
  };

  const archive = (notification: Notification, onArchived?: () => void) => {
    // execute UI follow-up (open next) immediately
    onArchived?.();
    // Archive notifications in the same scope (entity or single record).
    updateNotificationsByCondition({
      conditions: getNotificationScopeConditions(notification),
      patch: { status: NotificationStatus.Archived },
    });
  };

  const snooze = (notification: Notification) => {
    const conversationData = notification?.parsedPayload?.conversation;
    if (!conversationData) {
      return;
    }

    const conversationFriendlyId = getNotificationConversationFriendlyId(notification);

    showSnoozeModal({
      conversationId: notification.entityId,
      partnerId: conversationData.partnerId,
      friendlyId: conversationFriendlyId ?? undefined,
      subject: conversationData.subject,
    });
  };

  const copy = (text: string) => {
    void copyToClipboard(text);
  };

  const handleCopyUrl = (notification: Notification) => {
    const conversationData = notification?.parsedPayload?.conversation;
    if (!conversationData || !partners?.[conversationData.partnerId]) {
      return;
    }
    const conversationFriendlyId = getNotificationConversationFriendlyId(notification);

    if (!conversationFriendlyId) {
      return;
    }

    const partnerFriendlyId = getPartnerFriendlyIdentifier(
      partners[conversationData.partnerId],
    );
    const path = portalPaths.conversation.full(conversationFriendlyId);

    copy(`https://${window.location.host}/${partnerFriendlyId}${path}`);
  };

  const handleCopyId = (notification: Notification) => {
    const conversationFriendlyId = getNotificationConversationFriendlyId(notification);

    if (conversationFriendlyId) {
      copy(conversationFriendlyId);
    }
  };

  const openConversation = (notification: Notification) => {
    const conversationFriendlyId = getNotificationConversationFriendlyId(notification);
    if (conversationFriendlyId) {
      navigate(portalPaths.conversation.full(conversationFriendlyId));
    }
  };

  return {
    toggleRead,
    archive,
    snooze,
    handleCopyUrl,
    handleCopyId,
    openConversation,
  };
};

export const useNotificationMenuItems = () => {
  const { toggleRead, archive, snooze, handleCopyUrl, handleCopyId, openConversation } =
    useNotificationMethods();

  const getMenuItems = (
    notification: Notification,
    callbacks?: { onArchived?: () => void },
  ) => {
    const menuItems: EllipsesMenuOption[] = [
      {
        label:
          notification.status === NotificationStatus.Read ? 'Mark as unread' : 'Mark as read',
        onClick: () => toggleRead(notification),
      },
      {
        label: 'Archive',
        onClick: () => archive(notification, callbacks?.onArchived),
      },
    ];

    if (getNotificationEntityName(notification.entity) === EntityNames.Conversation) {
      menuItems.push(
        ...[
          {
            label: 'Snooze conversation',
            onClick: () => snooze(notification),
          },
          {
            label: 'Copy conversation url',
            onClick: () => handleCopyUrl(notification),
          },
          {
            label: 'Copy conversation id',
            onClick: () => handleCopyId(notification),
          },
          {
            label: 'Go to conversation',
            onClick: () => openConversation(notification),
          },
        ],
      );
    }

    return menuItems;
  };

  return {
    getMenuItems,
  };
};

export function getNotificationScopeConditions(notification: Notification) {
  if (isNullish(notification.entityId)) {
    return [
      { attribute: 'notificationId', operator: '=', value: notification.notificationId },
    ];
  }

  return [{ attribute: 'entityId', operator: '=', value: String(notification.entityId) }];
}
